import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, Send, Loader2, Check, X, Pencil, History, Plus, Lightbulb, MessageSquare, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { BRAND } from "@/config/brand";
import { resolveResaContext } from "@/config/resaPrompts";
import { resaService } from "@/services/resaService";
import { emitCrmChanged } from "@/services/crmService";

const ACTION_LABELS = {
  create_task: "Create task", create_followup: "Create follow-up", create_event: "Create calendar event",
  create_marketing_draft: "Create marketing draft", change_lead_stage: "Change lead stage",
};

/** RESA — Real Estate Strategic Assistant. A real, restrained assistant panel:
 * grounded Q&A over authorized data, proposed actions the user approves, and
 * on-brand marketing drafting. No autonomous writes. */
export const ResaPanel = ({ trigger }) => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [pageCtx, setPageCtx] = useState(() => resolveResaContext(pathname));
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState("chat");
  const [convos, setConvos] = useState([]);
  const [editing, setEditing] = useState({});
  const scrollRef = useRef(null);

  useEffect(() => { if (open) setPageCtx(resolveResaContext(pathname)); }, [open, pathname]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, loading]);

  const reset = useCallback(() => { setMessages([]); setConversationId(null); setError(null); setEditing({}); setView("chat"); }, []);

  const send = useCallback(async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setError(null);
    setInput("");
    setMessages((m) => [...m, { role: "user", content, _local: true }]);
    setLoading(true);
    try {
      const res = await resaService.chat({
        conversation_id: conversationId,
        content,
        context_type: pageCtx.context_type,
        context_id: pageCtx.context_id,
      });
      setConversationId(res.conversation.id);
      setMessages((m) => [...m, res.message]);
    } catch (err) {
      setError(err?.message || "RESA couldn't complete that request right now. Your AgentHQ data is still safe.");
    } finally {
      setLoading(false);
    }
  }, [input, loading, conversationId, pageCtx]);

  const openHistory = useCallback(async () => {
    setView("history");
    try { setConvos((await resaService.listConversations()).conversations || []); } catch { setConvos([]); }
  }, []);

  const loadConversation = useCallback(async (id) => {
    setLoading(true);
    try {
      const res = await resaService.getConversation(id);
      setConversationId(res.conversation.id);
      setMessages(res.messages || []);
      setView("chat");
    } catch (err) { toast.error(err?.message || "Could not load conversation"); }
    finally { setLoading(false); }
  }, []);

  const updateActionInList = (actionId, patch) => {
    setMessages((msgs) => msgs.map((m) => ({
      ...m,
      proposed_actions: (m.proposed_actions || []).map((a) => (a.id === actionId ? { ...a, ...patch } : a)),
    })));
  };

  const approve = useCallback(async (action) => {
    updateActionInList(action.id, { _busy: true });
    try {
      const edited = editing[action.id] || null;
      const res = await resaService.approveAction(action.id, edited);
      updateActionInList(action.id, { status: res.action.status, _busy: false });
      setEditing((e) => { const n = { ...e }; delete n[action.id]; return n; });
      emitCrmChanged();
      toast.success(`${ACTION_LABELS[action.action_type] || "Action"} completed`);
    } catch (err) {
      updateActionInList(action.id, { _busy: false });
      toast.error(err?.message || "Could not complete that action");
    }
  }, [editing]);

  const reject = useCallback(async (action) => {
    updateActionInList(action.id, { _busy: true });
    try {
      await resaService.rejectAction(action.id);
      updateActionInList(action.id, { status: "rejected", _busy: false });
    } catch (err) {
      updateActionInList(action.id, { _busy: false });
      toast.error(err?.message || "Could not cancel");
    }
  }, []);

  const startEdit = (action) => setEditing((e) => ({ ...e, [action.id]: { ...action.proposed_fields } }));
  const cancelEdit = (id) => setEditing((e) => { const n = { ...e }; delete n[id]; return n; });

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger || (
          <button
            type="button"
            data-testid="resa-trigger"
            className="flex items-center gap-2 rounded-lg border border-brand-navy/15 bg-brand-navy px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-deep-blue"
            aria-label="Open RESA"
          >
            <Sparkles className="h-4 w-4 text-brand-gold" />
            <span className="hidden sm:inline">{BRAND.assistant}</span>
          </button>
        )}
      </SheetTrigger>
      <SheetContent className="flex w-full flex-col p-0 sm:max-w-md" data-testid="resa-panel">
        <SheetTitle className="sr-only">{BRAND.assistant} — {BRAND.assistantFull}</SheetTitle>
        <SheetDescription className="sr-only">Ask RESA about your business and approve suggested actions.</SheetDescription>

        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border bg-brand-navy px-5 py-4 text-white">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-white/10 text-brand-gold">
            <Sparkles className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-brand text-base font-semibold leading-none">{BRAND.assistant}</p>
            <p className="mt-1 truncate text-xs text-white/60">{BRAND.assistantFull}</p>
          </div>
          <button onClick={reset} title="New chat" data-testid="resa-new-chat" className="rounded-md p-1.5 text-white/70 hover:bg-white/10 hover:text-white"><Plus className="h-4 w-4" /></button>
          <button onClick={openHistory} title="History" data-testid="resa-history-btn" className="rounded-md p-1.5 text-white/70 hover:bg-white/10 hover:text-white"><History className="h-4 w-4" /></button>
        </div>

        {view === "history" ? (
          <div className="flex-1 overflow-y-auto p-4" data-testid="resa-history">
            <button onClick={() => setView("chat")} className="mb-3 text-xs font-medium text-accent hover:underline">← Back to chat</button>
            {convos.length === 0 ? (
              <p className="mt-6 text-center text-sm text-muted-foreground">No previous conversations yet.</p>
            ) : convos.map((c) => (
              <button key={c.id} onClick={() => loadConversation(c.id)} data-testid="resa-history-item"
                className="mb-2 flex w-full items-center gap-2 rounded-lg border border-border p-3 text-left text-sm hover:bg-secondary">
                <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{c.title}</span>
              </button>
            ))}
          </div>
        ) : (
          <>
            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4" data-testid="resa-messages">
              {messages.length === 0 && !loading && (
                <div className="flex flex-col items-center gap-3 px-4 pt-6 text-center" data-testid="resa-empty">
                  <div className="grid h-12 w-12 place-items-center rounded-full bg-secondary text-brand-gold"><Sparkles className="h-6 w-6" /></div>
                  <p className="font-brand text-lg font-semibold text-foreground">Meet {BRAND.assistant}</p>
                  <p className="text-sm font-medium text-muted-foreground">Your {BRAND.assistantFull}</p>
                  <p className="max-w-xs text-sm leading-relaxed text-muted-foreground">
                    RESA helps you understand and act on information already inside AgentHQ — no guesswork, no busywork.
                  </p>
                </div>
              )}

              {messages.map((m, i) => (
                <div key={m.id || i} data-testid={`resa-msg-${m.role}`}>
                  {m.role === "user" ? (
                    <div className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-sm bg-brand-navy px-3.5 py-2 text-sm text-white">{m.content}</div>
                  ) : (
                    <div className="space-y-3">
                      <div className="w-fit max-w-[92%] whitespace-pre-wrap rounded-2xl rounded-bl-sm bg-secondary px-3.5 py-2.5 text-sm leading-relaxed text-foreground">{m.content}</div>
                      {m.structured_metadata?.insights?.length > 0 && (
                        <div className="rounded-lg border border-accent/30 bg-accent/5 px-3 py-2" data-testid="resa-insight">
                          <p className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-accent"><Lightbulb className="h-3 w-3" /> RESA Insight</p>
                          <ul className="space-y-1 text-xs text-foreground">
                            {m.structured_metadata.insights.map((ins, k) => <li key={k} className="flex gap-1.5"><span className="text-accent">•</span><span>{ins}</span></li>)}
                          </ul>
                        </div>
                      )}
                      {(m.proposed_actions || []).map((a) => (
                        <ProposedActionCard key={a.id} action={a} editing={editing[a.id]}
                          onApprove={() => approve(a)} onReject={() => reject(a)}
                          onStartEdit={() => startEdit(a)} onCancelEdit={() => cancelEdit(a.id)}
                          onFieldChange={(k, v) => setEditing((e) => ({ ...e, [a.id]: { ...e[a.id], [k]: v } }))} />
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="resa-loading"><Loader2 className="h-4 w-4 animate-spin" /> RESA is thinking…</div>}
              {error && (
                <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive" data-testid="resa-error">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>{error}</span>
                </div>
              )}
            </div>

            {/* Suggested prompts */}
            {messages.length === 0 && !loading && (
              <div className="flex flex-wrap gap-2 border-t border-border px-4 py-3" data-testid="resa-suggestions">
                {pageCtx.prompts.map((p) => (
                  <button key={p} onClick={() => send(p)} data-testid="resa-suggestion"
                    className="rounded-full border border-border bg-background px-3 py-1.5 text-xs text-foreground transition-colors hover:border-accent hover:text-accent">{p}</button>
                ))}
              </div>
            )}

            {/* Composer */}
            <div className="border-t border-border p-3">
              <div className="flex items-end gap-2">
                <Textarea
                  data-testid="resa-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={`Ask ${BRAND.assistant} about your business…`}
                  rows={1}
                  className="max-h-32 min-h-[42px] resize-none"
                />
                <Button size="icon" onClick={() => send()} disabled={loading || !input.trim()} data-testid="resa-send" className="h-[42px] w-[42px] shrink-0 bg-brand-navy hover:bg-brand-deep-blue">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
              <p className="mt-2 text-center text-[10px] text-muted-foreground">RESA uses your authorized AgentHQ data. It never sends messages or publishes without your approval.</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

const STATUS_STYLES = {
  executed: { label: "Approved", cls: "border-emerald-300 bg-emerald-50 text-emerald-700" },
  rejected: { label: "Cancelled", cls: "border-border bg-secondary text-muted-foreground" },
  failed: { label: "Failed", cls: "border-destructive/30 bg-destructive/5 text-destructive" },
};

const ProposedActionCard = ({ action, editing, onApprove, onReject, onStartEdit, onCancelEdit, onFieldChange }) => {
  const done = ["executed", "rejected", "failed"].includes(action.status);
  const badge = STATUS_STYLES[action.status];
  const editableKeys = Object.keys(action.proposed_fields || {}).filter((k) => !k.endsWith("_id"));

  return (
    <div className="rounded-lg border border-brand-gold/40 bg-brand-gold/5 p-3" data-testid="resa-action-card">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-brand-navy"><Sparkles className="h-3 w-3 text-brand-gold" /> Proposed Action</p>
        {badge && <Badge variant="outline" className={badge.cls}>{badge.label}</Badge>}
      </div>
      <p className="mt-1.5 text-sm font-semibold text-foreground">{action.title}</p>
      {action.summary && <p className="text-xs text-muted-foreground">{action.summary}</p>}

      {editing ? (
        <div className="mt-2 space-y-2" data-testid="resa-action-edit">
          {editableKeys.map((k) => (
            <div key={k}>
              <label className="text-[11px] font-medium capitalize text-muted-foreground">{k.replace(/_/g, " ")}</label>
              <Input value={editing[k] ?? ""} onChange={(e) => onFieldChange(k, e.target.value)} className="h-8 text-sm" data-testid={`resa-edit-${k}`} />
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-2 space-y-0.5">
          {editableKeys.map((k) => (
            <p key={k} className="text-xs text-foreground"><span className="capitalize text-muted-foreground">{k.replace(/_/g, " ")}:</span> {String(action.proposed_fields[k] ?? "")}</p>
          ))}
        </div>
      )}

      {action.status === "failed" && action.error && <p className="mt-1 text-xs text-destructive">{action.error}</p>}

      {!done && (
        <div className="mt-3 flex gap-2">
          <Button size="sm" onClick={onApprove} disabled={action._busy} data-testid="resa-action-approve" className="h-8 bg-brand-navy hover:bg-brand-deep-blue">
            {action._busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Approve
          </Button>
          {editing ? (
            <Button size="sm" variant="outline" onClick={() => onCancelEdit()} data-testid="resa-action-doneedit" className="h-8">Done editing</Button>
          ) : (
            <Button size="sm" variant="outline" onClick={onStartEdit} disabled={action._busy || editableKeys.length === 0} data-testid="resa-action-edit-btn" className="h-8"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
          )}
          <Button size="sm" variant="ghost" onClick={onReject} disabled={action._busy} data-testid="resa-action-cancel" className="h-8 text-muted-foreground"><X className="h-3.5 w-3.5" /> Cancel</Button>
        </div>
      )}
    </div>
  );
};
